import React from 'react';
import { Link } from 'react-router-dom';
import {
    Badge,
    HeadingText30,
    BodyTextXSmall,
    BodyTextSmall,
    BodyTextLarge,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import moment from 'moment';
import { Conversations } from '@modernhealth/messaging-kit';

import { IndicatorDot } from 'src/shared/components/IndicatorDot';
import { Avatar } from 'src/modules/messaging/components/Avatar';

const StyledLink = styled(Link, { shouldForwardProp: prop => prop !== 'active' })(
    ({ active }: { active: boolean }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: pxToRem(21),
        paddingLeft: pxToRem(15),
        borderLeft: `transparent ${pxToRem(6)} solid`,
        borderLeftColor: active ? colors.blue55 : 'transparent',
        color: colors.gray85,
        backgroundColor: active ? colors.blue05 : undefined,
        borderRadius: pxToRem(8),
        ':hover': {
            color: 'initial',
        },
    }),
);

const ContentColumn = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
});

const ContentRow = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

const ConversationIndicatorDot = styled(IndicatorDot)({
    marginLeft: pxToRem(5),
    alignSelf: 'center',
});

const ConversationAvatar = styled(Avatar)({
    marginRight: pxToRem(14),
});

const Message = styled(BodyTextSmall)(({ unread }: { unread: boolean }) => ({
    flexGrow: 1,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    width: 0,
    fontWeight: unread ? 500 : undefined,
}));

const DateText = styled(BodyTextXSmall)({
    paddingLeft: pxToRem(5),
    whiteSpace: 'nowrap',
});

interface Props {
    conversation: Conversations[number];
    to: string;
    active: boolean;
}

export const ConversationListItem: React.FC<Props> = ({ conversation, to, active }) => {
    const { t } = useTranslation();
    const date = moment(conversation.newestMessage?.sentAt || conversation.createdAt).format(
        'MMM D',
    );
    const name = `${conversation.coach?.firstName} ${conversation.coach?.lastName}`;
    const HeadingText = conversation.hasUnreadMessages ? HeadingText30 : BodyTextLarge;

    return (
        <StyledLink
            to={to}
            active={active}
            aria-current={active ? 'page' : undefined}
            aria-label={t(
                `messaging:conversation.aria.label${
                    conversation.hasUnreadMessages ? '.unread' : ''
                }`,
                {
                    name,
                },
            )}
        >
            <ConversationAvatar diameter={60} user={conversation.coach} />
            <ContentColumn>
                <ContentRow>
                    <HeadingText
                        as="h3"
                        marginBottom={5}
                        fontColor={conversation.hasUnreadMessages || active ? 'gray85' : 'gray65'}
                    >
                        {name}
                    </HeadingText>
                    <DateText
                        fontColor={conversation.hasUnreadMessages || active ? 'gray85' : 'gray55'}
                    >
                        {date}
                    </DateText>
                </ContentRow>
                <ContentRow>
                    {conversation.newestMessage ? (
                        <Message
                            unread={conversation.hasUnreadMessages}
                            fontColor={
                                conversation.hasUnreadMessages || active ? 'gray85' : 'gray65'
                            }
                        >
                            {conversation.newestMessage.content}
                        </Message>
                    ) : (
                        <Badge variant="success" title={t('messaging:label.new_match')} />
                    )}
                    {conversation.hasUnreadMessages && <ConversationIndicatorDot diameter={10} />}
                </ContentRow>
            </ContentColumn>
        </StyledLink>
    );
};
